"use client";
import { hamtaSessionSvar } from "@/lib/klient-session";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState, useSyncExternalStore } from "react";
import MobileQrButton from "@/components/mobile/MobileQrButton";
import FormelbladButton from "@/components/layout/FormelbladButton";
import LektionsBarForElev from "@/components/layout/LektionsBarForElev";
import HogtidKnapp from "@/components/hogtid/HogtidKnapp";
import BugReportButton from "@/components/layout/BugReportButton";
import CalculatorButton from "@/components/calculator/CalculatorButton";
import GraphButton from "@/components/graph/GraphButton";
import { useProvNavMobilparning } from "@/components/layout/useProvNavMobilparning";
import { arMobiltelefon } from "@/lib/spel/enhet";
import {
  HEADER_INTRO_ATTR,
  HEADER_INTRO_KEY,
  HEADER_INTRO_LEFT_AT,
  HEADER_INTRO_TAIL,
  HEADER_INTRO_TOOLS_AT,
} from "@/lib/header-intro";

interface User {
  id: string;
  name: string | null;
  email: string;
  role: string;
  impersonating?: {
    studentId: string;
    studentName: string;
    klassId: string;
  };
}

interface SessionResponse {
  user: User | null;
  isAdmin?: boolean;
  viaLoginAs?: boolean;
}

export default function Nav() {
  const pathname = usePathname();
  const isLoginPage = pathname === "/login";
  // Exact segment, not a prefix: "/m" swallowed /mina-prov and the whole
  // header vanished on the elev's prov list (Erik, 2026-09-14).
  const isMobilePairedView = pathname === "/m" || (pathname?.startsWith("/m/") ?? false);
  // Provrent header (fas 3B UX-skärm, spec "Provläget"): an elev on
  // /prov-elev/[passId] must see ONLY the exam-safe controls — no course
  // links, lektion bar, or account menu beyond logging out. Decided from
  // pathname alone (never from the session fetch below) so nothing normal
  // flashes before the session response arrives; this path is elev-only by
  // construction (proxy + provLageGrind already grind everything else away).
  const isProvElevView = pathname?.startsWith("/prov-elev/") ?? false;
  const provNav = useProvNavMobilparning(isProvElevView);
  // Under ett aktivt pass som förbjuder mobilparning svarar ikonen med en
  // förbjudet-popup i stället för QR-koden — headern i övrigt är identisk.
  const mobilForbjudenIProv = isProvElevView && provNav.aktivt && !provNav.mobilTillaten;
  // A phone cannot pair itself as a second device, so the pairing icon is
  // hidden there (the button stays mounted: its pairing-status poll keeps
  // running exactly as before). Read from the user agent after hydration;
  // the server snapshot is "not a phone", so nothing mismatches.
  const arMobil = useSyncExternalStore(
    () => () => {},
    () => arMobiltelefon(navigator.userAgent),
    () => false,
  );
  const [user, setUser] = useState<User | null>(null);
  // False until the session answer has arrived: until then nobody is known to be logged out,
  // so "Logga in" is not shown — it flashed for every logged-in student on each full page
  // load (stress test 2026-11-01, F-2026-015).
  const [sessionKand, setSessionKand] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  // True only in the tab that was opened for student impersonation. The
  // impersonate flow opens a new tab at "/?as=elev"; we persist that marker
  // in sessionStorage (tab-scoped) so it survives navigation away from "/".
  // The teacher's own tab never carries the marker and therefore keeps the
  // teacher header even though the impersonation cookie is shared globally.
  const [isAsElevTab, setIsAsElevTab] = useState(false);
  // True when the current session is itself a borrowed identity (an
  // admin/skol-admin has logged in as this user). Then every level down to the
  // deepest is an impersonation, so the header shows the deepest identity in
  // ALL tabs — there is no "own" tab to protect (unlike a genuine teacher).
  const [viaLoginAs, setViaLoginAs] = useState(false);

  // A visit to /login means a new login follows — let the intro play again.
  useEffect(() => {
    if (!isLoginPage) return;
    try {
      localStorage.removeItem(HEADER_INTRO_KEY);
    } catch {}
  }, [isLoginPage]);

  // Once the tools are on screen, align their entrance with the bar's clock
  // (they mount only after the session fetch, so a fixed CSS delay would
  // drift) and drop the intro attribute when the sequence is over, so later
  // re-mounts (tab switches, role changes) never replay it.
  useEffect(() => {
    if (!user) return;
    const root = document.documentElement;
    const t0 = root.getAttribute(HEADER_INTRO_ATTR);
    if (t0 === null) return;
    const delay = Math.max(60, Number(t0) + HEADER_INTRO_TOOLS_AT - performance.now());
    root.style.setProperty("--intro-tools", `${delay}ms`);
    // The lektion bar enters on its own beat after the tools, and mounts
    // after its own fetch. Hand it the time remaining until that beat when it
    // appears (zero if it arrives later). Mutation callbacks run before style
    // is computed, so the delay is in place for the first frame.
    const leftAt = Number(t0) + HEADER_INTRO_LEFT_AT;
    const left = document.querySelector<HTMLElement>("[data-intro-left]");
    const syncLeft = () =>
      left?.style.setProperty(
        "--intro-left",
        `${Math.max(0, leftAt - performance.now())}ms`,
      );
    syncLeft();
    const observer = new MutationObserver(() => {
      syncLeft();
      observer.disconnect();
    });
    if (left) observer.observe(left, { childList: true });
    const done = window.setTimeout(() => {
      root.removeAttribute(HEADER_INTRO_ATTR);
      root.style.removeProperty("--intro-tools");
      left?.style.removeProperty("--intro-left");
    }, delay + HEADER_INTRO_TAIL);
    return () => {
      observer.disconnect();
      window.clearTimeout(done);
    };
  }, [user]);

  useEffect(() => {
    if (isLoginPage || isMobilePairedView) return;
    hamtaSessionSvar<SessionResponse>()
      .then((data) => {
        // A failed fetch falls back to the old behaviour: show "Logga in".
        setSessionKand(true);
        if (!data) return;
        setUser(data.user);
        setIsAdmin(Boolean(data.isAdmin));
        setViaLoginAs(Boolean(data.viaLoginAs));
        // Resolve the tab-scoped impersonation marker together with the
        // session. The impersonate flow opens a new tab at "/?as=elev"; we
        // persist that marker so it survives navigation away from "/". When
        // impersonation has ended (no cookie) we drop the marker so the header
        // reverts to the impersonator.
        if (typeof window !== "undefined") {
          const params = new URLSearchParams(window.location.search);
          if (params.get("as") === "elev") {
            sessionStorage.setItem("vektr-as-elev", "1");
          }
          if (data.user && !data.user.impersonating) {
            sessionStorage.removeItem("vektr-as-elev");
          }
          setIsAsElevTab(sessionStorage.getItem("vektr-as-elev") === "1");
        }
      });
  }, [isLoginPage, isMobilePairedView]);

  // Skol-admin gets the same stripped-down nav as super-admin (no student
  // tools), but home links to /skoladmin instead of /admin.
  const isSkolAdmin = user?.role === "skoladmin";
  // Show the impersonated student (the deepest level) when impersonating AND
  // either (a) this is the tab that entered impersonation (?as=elev marker), or
  // (b) the session is itself a login-as chain (admin→lärare→elev) where the
  // header should always reflect the deepest level. In both cases the header
  // belongs to the student and home links keep "?as=elev" so "/" renders the
  // student overview instead of the (impersonated) teacher dashboard.
  const isImpersonatingStudent =
    Boolean(user?.impersonating) && (isAsElevTab || viaLoginAs);
  const homeHref = isAdmin
    ? "/admin"
    : isSkolAdmin
      ? "/skoladmin"
      : isImpersonatingStudent
        ? "/?as=elev"
        : "/";
  const accountName = isImpersonatingStudent
    ? user!.impersonating!.studentName
    : user?.name || user?.email;
  const hideStudentTools = isAdmin || isSkolAdmin;

  if (isMobilePairedView) return null;

  // Projektor-vyn (elev-vy för storbild, spel-projektorn) renderar egen
  // vektr-header och skippar global Nav helt — annars dubbla loggor.
  const isProjektorVy =
    pathname?.match(/^\/klass\/[^/]+\/(lektion\/elev-vy|spel\/[^/]+\/projektor|genomgang\/[^/]+\/projektor|reflektion\/[^/]+\/projektor|begreppsjakt\/[^/]+\/projektor|mattedetektiv\/[^/]+\/projektor|problemlosning\/[^/]+\/projektor)$/) ||
    pathname === "/larare/genomgang/forhandsvisning/projektor";
  if (isProjektorVy) return null;

  return (
    <nav
      data-intro-bar
      className="sticky top-0 z-30 bg-[var(--color-nav-bg)] border-b border-[var(--color-nav-border)]">
      <div className="relative h-16 flex items-center justify-end px-6 max-sm:px-4">
        {/* Vänster-grupp: lektions-bar för elever (lärare i normalt läge
            har ingen "egen" lektion och hoppas över). Impersonation-status
            visas via SessionImpersonationBanner under Nav. */}
        {user ? (
          <div data-intro-left className="absolute left-6 flex items-center gap-4 max-sm:left-4 max-sm:gap-3">
            <MobilLogga href={homeHref} />
            <HogtidKnapp />
            {!hideStudentTools && (user.role !== "larare" || isImpersonatingStudent) && (
              <LektionsBarForElev provPagar={isProvElevView && provNav.aktivt} />
            )}
          </div>
        ) : (
          <div className="absolute left-6 flex items-center gap-4 max-sm:left-4 max-sm:gap-3">
            <div className="md:hidden">
              <MobilLogga href={homeHref} />
            </div>
            <HogtidKnapp />
          </div>
        )}
        {/* Below md the centred logo collides with the tools (and the
            lektion bar), so phones get a small one on the left instead
            (MobilLogga) — and no intro. */}
        <Link
          href={homeHref}
          title={
            isImpersonatingStudent
              ? "Till elevens startsida"
              : "Till startsidan"
          }
          className="hidden md:block absolute left-1/2 -translate-x-1/2 font-[family-name:var(--font-logo)] text-[3.5578rem] leading-none text-[var(--color-nav-text)] hover:text-[var(--color-accent)] transition-colors"
        >
          {/* Inner span carries the intro's write-on mask; padding gives the
              script swashes room so the mask doesn't clip them. */}
          <span data-intro-logo className="inline-block px-[0.15em] pb-[0.2em] -mx-[0.15em] -mb-[0.2em]">
            vektr
          </span>
        </Link>
        <div className="flex items-center gap-3">
          {isLoginPage ? null : user ? (
            // Tre grupper: studie-verktyg [calc, formelblad] — system [mobil,
            // kugghjul, bug] — konto [namn]. Inom-grupp gap = 1 (4px,
            // tätt), mellan-grupp gap = 4 (16px) — tydlig visuell separation.
            // Logga ut ligger numera inne på Inställningar-sidan (header hålls
            // minimalistisk). För admin: bara Inställningar + namn. Inga elev/lärar-verktyg.
            <div className="flex items-center gap-4 max-sm:gap-2">
              {!hideStudentTools && (
                <div data-intro-g className="[--intro-g:0] flex items-center gap-1 max-sm:gap-0">
                  <CalculatorButton />
                  <GraphButton />
                  {/* Samma formelblad för elever och lärare — läraren ska
                      kunna slå upp bladet på samma villkor som klassen. */}
                  <FormelbladButton />
                </div>
              )}
              {!hideStudentTools && (
                <div data-intro-g className="[--intro-g:1] flex items-center gap-1">
                  <MobileQrButton forbjudenIProv={mobilForbjudenIProv} dold={arMobil} />
                  <BugReportButton />
                </div>
              )}
              <div data-intro-g className="[--intro-g:2] flex items-center gap-3">
                <Link
                  href="/installningar"
                  title="Öppna dina inställningar"
                  className="text-sm text-[var(--color-nav-text-muted)] hover:text-[var(--color-accent)] transition-colors hidden sm:inline"
                >
                  {accountName}
                </Link>
                {/* Phones have no room for the name: a gear leads to the
                    same settings page. */}
                <Link
                  href="/installningar"
                  title="Öppna dina inställningar"
                  aria-label="Inställningar"
                  className="sm:hidden p-1.5 rounded-md text-[var(--color-nav-text)] hover:text-[var(--color-accent)] transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                  </svg>
                </Link>
              </div>
            </div>
          ) : sessionKand ? (
            // data-intro-guest: hidden while the intro plays (which only plays for a
            // logged-in user).
            <Link
              href="/login"
              data-intro-guest
              title="Logga in på vektr"
              className="text-sm px-4 py-2 rounded-md bg-[var(--color-accent)] text-[var(--color-nav-bg)] font-medium hover:opacity-90 transition-opacity"
            >
              Logga in
            </Link>
          ) : null}
        </div>
      </div>
    </nav>
  );
}

/** Small wordmark at the left of the header, below md only. */
function MobilLogga({ href }: { href: string }) {
  return (
    <Link
      href={href}
      title="Till startsidan"
      className="mobil-logga md:hidden font-[family-name:var(--font-logo)] text-[2.1rem] leading-none text-[var(--color-nav-text)] hover:text-[var(--color-accent)] transition-colors"
    >
      vektr
    </Link>
  );
}
